// ── Spectator HUD ──
// Overlay shown when the player is in spectator mode.
// Displays: target name, mode indicator, player list, and control hints.

import { type SpectatorMode } from './spectator';

const MODE_LABELS: Record<SpectatorMode, string> = {
  fpp_follow: 'FIRST PERSON',
  tpp_follow: 'THIRD PERSON',
  free_fly: 'FREE FLY',
};

export interface SpectatorPlayerInfo {
  id: number;
  name: string;
  alive: boolean;
  team: number; // 1=T, 2=CT
  isTarget: boolean;
}

export class SpectatorHUD {
  private container: HTMLElement;
  private targetNameEl: HTMLElement;
  private modeIndicatorEl: HTMLElement;
  private playerListEl: HTMLElement;
  private hintEl: HTMLElement;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'spectator-hud';
    this.container.style.cssText = [
      'position: absolute',
      'top: 0',
      'left: 0',
      'width: 100%',
      'height: 100%',
      'pointer-events: none',
      'z-index: 20',
      'font-family: "Courier New", monospace',
      'display: none',
    ].join('; ');

    // "SPECTATING: PlayerName" banner at top-center
    this.targetNameEl = document.createElement('div');
    this.targetNameEl.style.cssText = [
      'position: absolute',
      'top: 80px',
      'left: 50%',
      'transform: translateX(-50%)',
      'font-size: 16px',
      'color: #ccc',
      'text-shadow: 1px 1px 3px #000',
      'background: rgba(0, 0, 0, 0.5)',
      'padding: 6px 20px',
      'border-radius: 3px',
      'letter-spacing: 2px',
      'white-space: nowrap',
    ].join('; ');
    this.targetNameEl.textContent = 'SPECTATING';
    this.container.appendChild(this.targetNameEl);

    // Mode indicator below target name
    this.modeIndicatorEl = document.createElement('div');
    this.modeIndicatorEl.style.cssText = [
      'position: absolute',
      'top: 112px',
      'left: 50%',
      'transform: translateX(-50%)',
      'font-size: 11px',
      'color: #888',
      'text-shadow: 1px 1px 2px #000',
      'letter-spacing: 1px',
    ].join('; ');
    this.modeIndicatorEl.textContent = 'FIRST PERSON';
    this.container.appendChild(this.modeIndicatorEl);

    // Player list (right side)
    this.playerListEl = document.createElement('div');
    this.playerListEl.style.cssText = [
      'position: absolute',
      'top: 140px',
      'right: 14px',
      'font-size: 12px',
      'display: flex',
      'flex-direction: column',
      'gap: 2px',
      'max-height: 300px',
      'overflow-y: auto',
    ].join('; ');
    this.container.appendChild(this.playerListEl);

    // Control hints at bottom-center
    this.hintEl = document.createElement('div');
    this.hintEl.style.cssText = [
      'position: absolute',
      'bottom: 80px',
      'left: 50%',
      'transform: translateX(-50%)',
      'font-size: 11px',
      'color: #666',
      'text-shadow: 1px 1px 2px #000',
      'text-align: center',
      'letter-spacing: 1px',
      'line-height: 1.6',
    ].join('; ');
    this.hintEl.innerHTML = [
      'LMB / RMB &mdash; Next / Prev Player',
      'SPACE &mdash; Change View Mode',
      'WASD &mdash; Fly (in Free Fly mode)',
    ].join('<br>');
    this.container.appendChild(this.hintEl);

    document.body.appendChild(this.container);
  }

  show(): void {
    this.container.style.display = 'block';
  }

  hide(): void {
    this.container.style.display = 'none';
  }

  isVisible(): boolean {
    return this.container.style.display !== 'none';
  }

  /** Update the target player name display. */
  updateTarget(name: string): void {
    this.targetNameEl.textContent = `SPECTATING: ${name}`;
  }

  /** Update the current mode indicator. */
  updateMode(mode: SpectatorMode): void {
    this.modeIndicatorEl.textContent = MODE_LABELS[mode];
  }

  /** Update the player list sidebar. */
  updatePlayerList(players: SpectatorPlayerInfo[]): void {
    this.playerListEl.innerHTML = '';

    // Sort: alive first, then by team, then by name
    const sorted = [...players].sort((a, b) => {
      if (a.alive !== b.alive) return a.alive ? -1 : 1;
      if (a.team !== b.team) return a.team - b.team;
      return a.name.localeCompare(b.name);
    });

    for (const p of sorted) {
      const row = document.createElement('div');

      // Color: T = orange, CT = blue, dead = gray
      let color: string;
      if (!p.alive) {
        color = '#555';
      } else if (p.team === 1) {
        color = '#d4943a';
      } else if (p.team === 2) {
        color = '#5a9de6';
      } else {
        color = '#999';
      }

      const highlight = p.isTarget ? 'background: rgba(255,255,255,0.08); font-weight: bold;' : '';
      const aliveIcon = p.alive ? '&#9679;' : '&#9675;'; // filled/empty circle
      const targetArrow = p.isTarget ? ' &#9664;' : ''; // left-pointing triangle

      row.style.cssText = [
        `color: ${color}`,
        'padding: 2px 8px',
        'border-radius: 2px',
        'white-space: nowrap',
        highlight,
      ].join('; ');

      row.innerHTML = `<span style="font-size:8px;margin-right:4px;">${aliveIcon}</span>${this.escapeHtml(p.name)}${targetArrow}`;

      this.playerListEl.appendChild(row);
    }
  }

  dispose(): void {
    this.container.remove();
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
